/* eslint-disable max-lines */
import PropTypes from 'prop-types'
import React from 'react'
import Row from 'react-bootstrap/Row'
import { connect } from 'react-redux'
import { hardwareKeyDown, hardwareKeyUp,
  mouseDown, mouseMove, mouseUp, mouseWheel,
  windowBlur, Platforms } from '../actions/toolbar'
import { clipboardPaste } from '../actions/clipboard'
import { scaleMouseCursor } from '../lib/MouseCursorScaling'
import { Permissions } from '../lib/Permissions'
import { VideoElement } from '../lib/VideoElement'
import { gracefulExit } from '../actions/disconnect'
import '../styles/ondemand.scss'
import { logger } from '../lib/utils/logger'
import { shouldPreventDefaultForKeyDown } from '../epics/shortcuts'

export class OnDemandComponent extends React.Component {

  constructor (props) {
    super(props)

    this._mouseCursor = 'default'
    this._cursorImage = null
    this._ratio = 0
    this._videoElement = new VideoElement()
    this._isInsideActiveArea = false
  }

  /** @type {?HTMLMediaElement} */
  _video

  _audio

  _videoOnRefChanged = (id) => {
    this._video = id
    this._videoElement.setVideo(id)
  }

  _audioOnRefChanged = (element) => {
    this._audio = element
  }

  componentDidMount () {
    const {
      videoStream,
      gracefulExitAction,
    } = this.props

    const rootDiv = document.getElementById('root')

    if (this._video.srcObject !== videoStream) {
      this._video.srcObject = videoStream
      logger('VIDEO ==== set on video element')
    }

    this.setupAudio()
    this._audio.defaultMuted = true

    // In fullscreen mode, the root div in firefox has an extra pixel on top
    // (This is a firefox bug: https://bugzilla.mozilla.org/show_bug.cgi?id=729011)
    // The window.fullScreen property is only valid in firefox, so we use it in order to truncate the div
    // and to prevent unnecessary scrollbars in fullscreen mode
    window.addEventListener('resize', () => {
      if (window.fullScreen) {
        rootDiv.style.marginTop = '-1px'
      } else {
        rootDiv.style.marginTop = '0px'
      }
    })

    window.addEventListener('beforeunload', gracefulExitAction)

    this.applyHandlers()
  }

  setupAudio () {
    if (this.props.features.audioChat && this._audio) {
      if (this._audio.srcObject !== this.props.audioStream) {
        this._audio.srcObject = this.props.audioStream
        if (this.props.audioStream) {
          logger(`incoming audio stream ${this.props.audioStream.id}`, this._audio.srcObject)
          this.props.audioStream.getAudioTracks().forEach((track) => {
            track.onunmute = () => {
              logger(`  --- audio track unmuted ${track.id}`)
            }

            track.onmute = () => {
              logger(`  --- audio track muted ${track.id}`)
            }
          })
          this._audio.volume = 1
        } else {
          logger('incoming audio stream null', this._audio.srcObject)
        }
      }
      if (this._audio.muted === this.props.audio.chatStarted) {
        this._audio.muted = !this.props.audio.chatStarted
        logger(`ui audio ${this._audio.muted ? 'disabled' : 'enabled'}`)
      }
      if (this._audio.paused === this.props.audio.chatStarted) {
        if (this.props.audio.chatStarted) {
          this._audio.play()
            .catch(() => {
              logger('- pause audio play during loading')
            })
        } else {
          this._audio.pause()
        }
        logger(`ui audio playing ${!this._audio.paused}`)
      }
    }
  }

  componentWillUnmount () {
    this._videoElement.finalize()

    this._video.removeEventListener('mouseup', this.filterMouseEvents)
    this._video.removeEventListener('mousedown', this.filterMouseEvents)
    this._video.removeEventListener('mousemove', this.filterMouseEvents)
    this._video.removeEventListener('wheel', this.filterMouseEvents)
    this._video.removeEventListener('contextmenu', this.props.contextmenuAction)

    window.removeEventListener('blur', this.props.windowBlurAction)

    document.removeEventListener('keyup', this.props.hardwareKeyUpAction)
    document.removeEventListener('keydown', this.handleHardwareKeyDownAction)
    window.removeEventListener('paste', this.props.clipboardPasteAction)
  }

  componentDidUpdate () {
    this.setupAudio()
    this.scaleAndUpdateMouseCursor()
  }

  setVideoCursorStyle = () => {
    if (this._video) {
      this._video.style.cursor = this._mouseCursor
    }
  }

  scaleAndUpdateMouseCursor = async () => {
    // called from
    // render -> the cursor/fit-to-screen changed, we need to recompute the cursor
    //           - scaling is async -> render will not receive the new cursor
    //                              -> need to call setVideoCursorStyle after scaling
    //           - fit-to-screen set some css in render which changes the video aspect ratio
    //                   -> at this moment the video aspect ratio is the old one
    //                   -> need to call this again from componentDidUpdate to compute with the new ratio
    // mouse events -> to set cursor for outside/inside active area
    if (this._isInsideActiveArea && this.props.cursor.cursorImage) {
      const ratio = this._videoElement.getRatio().xRatio
      if (this._cursorImage !== this.props.cursor.cursorImage || this._ratio !== ratio) {
        this._ratio = ratio
        try {
          const scaledMouseCursor = await scaleMouseCursor(this.props.cursor, ratio)
          this._mouseCursor = scaledMouseCursor
          this._cursorImage = this.props.cursor.cursorImage
          this.setVideoCursorStyle()
        } catch (_) {
          this._mouseCursor = 'default'
          this._cursorImage = 'default'
        }
      }
    } else {
      this._mouseCursor = 'default'
      this._cursorImage = 'default'
    }
    this.setVideoCursorStyle()
  }

  handleHardwareKeyDownAction = (event) => {
    return this.props.hardwareKeyDownAction(event, this.props)
  }

  applyHandlers () {
    if (Permissions.isMouseKbdAllowed()) {
      const {
        hardwareKeyUpAction,
        contextmenuAction,
        windowBlurAction,
        clipboardPasteAction,
      } = this.props

      const video = document.querySelector('video')

      const eventHandlerMap = [
        { target: window, event: 'paste', handler: clipboardPasteAction },
        { target: document, event: 'keyup', handler: hardwareKeyUpAction },
        { target: document, event: 'keydown', handler: this.handleHardwareKeyDownAction },
        { target: window, event: 'blur', handler: windowBlurAction },
        { target: video, event: 'mouseup', handler: this.filterMouseEvents },
        { target: video, event: 'mousedown', handler: this.filterMouseEvents },
        { target: video, event: 'mousemove', handler: this.filterMouseEvents },
        { target: video, event: 'wheel', handler: this.filterMouseEvents },
        { target: video, event: 'contextmenu', handler: contextmenuAction }
      ]
      eventHandlerMap
        .forEach(({ target, event, handler, options = {} }) => target && target.addEventListener(event, handler, options))
    }
  }

  filterMouseEvents = (event) => {
    const activeArea = this._videoElement.getActiveAreaRect()
    const scaleX = this._videoElement.getRatio().xRatio
    const scaleY = this._videoElement.getRatio().yRatio
    const newEvent = {
      type: event.type,
      offsetX: event.offsetX,
      offsetY: event.offsetY,
      deltaX: event.deltaX,
      deltaY: event.deltaY,
      button: event.button
    }

    const isInsideActiveArea = (activeArea.top <= newEvent.offsetY) && (activeArea.bottom >= newEvent.offsetY) &&
      (activeArea.left <= newEvent.offsetX) && (activeArea.right >= newEvent.offsetX)

    this._isInsideActiveArea = isInsideActiveArea

    if (isInsideActiveArea) {
      newEvent.offsetX = scaleX * (newEvent.offsetX - activeArea.left)
      newEvent.offsetY = scaleY * (newEvent.offsetY - activeArea.top)

      switch (newEvent.type) {
        case 'wheel':
          event.preventDefault()
          newEvent.deltaX *= scaleX
          newEvent.deltaY *= scaleY
          event.preventDefault()
          this.props.mouseWheelAction(newEvent)
          break
        case 'mouseup':
          event.preventDefault()
          this.props.mouseUpAction(newEvent)
          break
        case 'mousedown':
          event.preventDefault()
          this.props.mouseDownAction(newEvent)
          break
        case 'mousemove':
          this.props.mouseMoveAction(newEvent)
          break
        default:
          break
      }
    }
    this.scaleAndUpdateMouseCursor()
  }

  iosStreamMuted () {
    const {
      webrtcConnection,
      platformType,
      screenSharing,
    } = this.props

    return (
      (platformType === Platforms.IOS) &&
      (webrtcConnection.accessState === 'ACCESS_GRANTED') &&
      !screenSharing
    )
  }

  render () {
    const {
      fitToScreen,
      features,
    } = this.props
    const rowFitToScreen = fitToScreen ? 'videoContainerFitToScreen' : ''
    const videoClassName = fitToScreen ? 'videoFitToScreen' : ''
    const iosStreamMuted = this.iosStreamMuted()

    this.scaleAndUpdateMouseCursor()

    return (
      <>
        <Row className={`${rowFitToScreen} nopadding videoContainer`}>
          <video tabIndex='-1' controlsList='nodownload'
            ref= {this._videoOnRefChanged} autoPlay='{true}' style={{ cursor: this._mouseCursor }}
            className={`${videoClassName} ${iosStreamMuted ? 'hidden' : ''}`} muted>
          </video>
          <audio ref={this._audioOnRefChanged}
            autoPlay={false}
            className={`audio-element ${features.audioChat ? '' : 'hidden'}`}
          >
          </audio>
        </Row>
      </>
    )
  }
}

OnDemandComponent.contextTypes = {
  t: PropTypes.func.isRequired
}

OnDemandComponent.propTypes = {
  videoStream: PropTypes.object,
  audioStream: PropTypes.object,
  webrtcConnection: PropTypes.object.isRequired,
  fitToScreen: PropTypes.bool.isRequired,
  cursor: PropTypes.object.isRequired,
  hardwareKeyUpAction: PropTypes.func.isRequired,
  hardwareKeyDownAction: PropTypes.func.isRequired,
  windowBlurAction: PropTypes.func.isRequired,
  clipboardPasteAction: PropTypes.func.isRequired,
  mouseUpAction: PropTypes.func.isRequired,
  mouseDownAction: PropTypes.func.isRequired,
  mouseMoveAction: PropTypes.func.isRequired,
  mouseWheelAction: PropTypes.func.isRequired,
  contextmenuAction: PropTypes.func.isRequired,
  gracefulExitAction: PropTypes.func.isRequired,
  screenSharing: PropTypes.bool.isRequired,
  platformType: PropTypes.string.isRequired,
  audio: PropTypes.object.isRequired,
  features: PropTypes.object.isRequired,
  clipboard: PropTypes.shape({
    enabled: PropTypes.bool.isRequired,
  }),
}

export function mapStateToProps (state) {
  return {
    videoStream: state.webrtcConnection.videoStream,
    audioStream: state.webrtcConnection.audioStream,
    webrtcConnection: state.webrtcConnection,
    fitToScreen: state.toolbar.fitToScreen,
    cursor: state.ondemand.cursor,
    screenSharing: state.ondemand.screenSharing,
    platformType: Object.values(Platforms).includes(state.signaling.agentDetails.platformType)
      ? state.signaling.agentDetails.platformType
      : 'windows',
    audio: state.audio,
    features: state.features,
    clipboard: {
      enabled: state.clipboard.enabled,
    },
  }
}

function mapDispatchToProps (dispatch) {
  return {
    hardwareKeyUpAction: (event) => {
      event.preventDefault()
      dispatch(hardwareKeyUp(event))
    },
    hardwareKeyDownAction: (event, props) => {
      if (shouldPreventDefaultForKeyDown(event, props)) {
        event.preventDefault()
      }
      dispatch(hardwareKeyDown(event))
    },
    windowBlurAction: () => {
      dispatch(windowBlur())
    },
    clipboardPasteAction: (event) => {
      event.preventDefault()
      dispatch(clipboardPaste(event))
    },
    mouseUpAction: (event) => {
      dispatch(mouseUp(event))
    },
    mouseDownAction: (event) => {
      dispatch(mouseDown(event))
    },
    mouseMoveAction: (event) => {
      dispatch(mouseMove(event))
    },
    mouseWheelAction: (event) => {
      dispatch(mouseWheel(event))
    },
    contextmenuAction: (event) => {
      event.preventDefault()
      return false
    },
    gracefulExitAction: () => {
      dispatch(gracefulExit())
    },
  }
}

export const OnDemand = connect(
  mapStateToProps,
  mapDispatchToProps
)(OnDemandComponent)
